import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { ScanTagAnimation } from './ScanTagAnimation';

interface NfcTagReaderProps {
  onAuthenticated: (key: string) => void;
  className?: string;
}

export const NfcTagReader: FC<NfcTagReaderProps> = ({
  onAuthenticated,
  className,
}) => {
  const [buffer, setBuffer] = useState('');
  const hiddenInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const input = hiddenInputRef.current;
    if (!input) return;

    // Focus input on mount
    input.focus();

    // Keep focus when clicking anywhere
    const handleClick = (): void => input.focus();
    document.addEventListener('click', handleClick);

    // Refocus on blur with delay
    const handleBlur = (): NodeJS.Timeout =>
      setTimeout(() => input.focus(), 100);
    input.addEventListener('blur', handleBlur);

    return () => {
      document.removeEventListener('click', handleClick);
      input.removeEventListener('blur', handleBlur);
    };
  }, []);

  const formatKey = (key: string): string => {
    // New logic: Remove all whitespace characters
    const formatted = key.replace(/\s+/g, '');
    return formatted;
  };

  const handleInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setBuffer((prev) => prev + newValue);
      e.target.value = '';
    },
    [setBuffer],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && buffer) {
        e.preventDefault();
        onAuthenticated(formatKey(buffer));
        setBuffer('');
      }
    },
    [buffer, onAuthenticated],
  );

  return (
    <div className={className}>
      <input
        ref={hiddenInputRef}
        type="text"
        onChange={handleInput}
        onKeyDown={handleKeyDown}
        style={{ position: 'absolute', opacity: 0, width: 1, height: 1 }}
        autoComplete="off"
        inputMode="none"
        aria-label="NFC Tag Reader Input"
      />
      <ScanTagAnimation className="w-full h-full" />
    </div>
  );
};
